import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { API, useLanguage, useTheme } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowRight, ArrowLeft, Play, X, Check, Phone, Clock,
  Calendar, MessageSquare, Bell, TrendingUp, Users, Star,
  ChevronRight, Zap, Shield, RefreshCw, Sun, Moon, Globe,
  Sparkles, AlertTriangle, CheckCircle2, Send
} from "lucide-react";
import axios from "axios";

// ============================================================
// SECTOR CONFIGURATION
// ============================================================
const SECTORS = [
  {
    key: "barbiere", label: "Barbiere / Parrucchiere", icon: "scissors",
    businessName: "BarberShop Milano",
    services: [
      { name: "Taglio Uomo", price: 18, duration: 30 },
      { name: "Taglio + Barba", price: 28, duration: 45 },
      { name: "Barba", price: 12, duration: 20 },
      { name: "Colore", price: 35, duration: 60 },
    ],
    clientName: "Marco",
    clientPhone: "+39 320 123 4567",
    waGreeting: "Ciao, avete disponibilità per un taglio oggi?",
  },
  {
    key: "parrucchiere_donna", label: "Parrucchiere Donna", icon: "sparkles",
    businessName: "Hair Beauty Studio",
    services: [
      { name: "Taglio Donna", price: 30, duration: 45 },
      { name: "Piega", price: 20, duration: 30 },
      { name: "Colore + Taglio", price: 65, duration: 90 },
      { name: "Trattamento Cheratina", price: 80, duration: 60 },
    ],
    clientName: "Giulia",
    clientPhone: "+39 333 987 6543",
    waGreeting: "Buongiorno, posso prenotare taglio e piega per sabato?",
  },
  {
    key: "dentista", label: "Studio Dentistico", icon: "tooth",
    businessName: "Studio Dentistico Rossi",
    services: [
      { name: "Visita di Controllo", price: 50, duration: 30 },
      { name: "Pulizia Dentale", price: 80, duration: 45 },
      { name: "Sbiancamento", price: 250, duration: 60 },
      { name: "Otturazione", price: 120, duration: 45 },
    ],
    clientName: "Laura",
    clientPhone: "+39 348 555 1234",
    waGreeting: "Salve, vorrei prenotare una pulizia dentale",
  },
  {
    key: "estetica", label: "Centro Estetico", icon: "heart",
    businessName: "Beauty Center Elegance",
    services: [
      { name: "Manicure", price: 25, duration: 30 },
      { name: "Ceretta Gambe", price: 30, duration: 30 },
      { name: "Trattamento Viso", price: 60, duration: 60 },
      { name: "Massaggio Rilassante", price: 50, duration: 45 },
    ],
    clientName: "Chiara",
    clientPhone: "+39 340 222 8888",
    waGreeting: "Ciao! Avete disponibilità per un trattamento viso questa settimana?",
  },
  {
    key: "fisioterapia", label: "Studio Fisioterapia", icon: "activity",
    businessName: "FisioCenter Pro",
    services: [
      { name: "Prima Visita", price: 60, duration: 45 },
      { name: "Fisioterapia", price: 50, duration: 45 },
      { name: "Massoterapia", price: 45, duration: 30 },
      { name: "Rieducazione Posturale", price: 55, duration: 45 },
    ],
    clientName: "Andrea",
    clientPhone: "+39 345 111 2222",
    waGreeting: "Buongiorno, avrei bisogno di una seduta di fisioterapia",
  },
  {
    key: "veterinario", label: "Ambulatorio Veterinario", icon: "paw",
    businessName: "VetClinic Animal Care",
    services: [
      { name: "Visita Generale", price: 40, duration: 30 },
      { name: "Vaccinazione", price: 35, duration: 20 },
      { name: "Check-up Completo", price: 80, duration: 45 },
      { name: "Ecografia", price: 60, duration: 30 },
    ],
    clientName: "Alessia",
    clientPhone: "+39 328 444 5555",
    waGreeting: "Salve, devo portare il mio cane per la vaccinazione annuale",
  },
  {
    key: "personal_trainer", label: "Personal Trainer / Palestra", icon: "dumbbell",
    businessName: "FitPro Training",
    services: [
      { name: "Sessione Individuale", price: 40, duration: 60 },
      { name: "Valutazione Fisica", price: 50, duration: 45 },
      { name: "Programma Mensile", price: 120, duration: 30 },
      { name: "Sessione Coppia", price: 60, duration: 60 },
    ],
    clientName: "Luca",
    clientPhone: "+39 339 666 7777",
    waGreeting: "Ciao, vorrei prenotare una sessione per domani",
  },
  {
    key: "psicologo", label: "Studio Psicologia", icon: "brain",
    businessName: "Studio Dott.ssa Bianchi",
    services: [
      { name: "Primo Colloquio", price: 60, duration: 50 },
      { name: "Seduta Individuale", price: 55, duration: 50 },
      { name: "Seduta di Coppia", price: 80, duration: 60 },
      { name: "Consulenza Online", price: 50, duration: 45 },
    ],
    clientName: "Sara",
    clientPhone: "+39 347 888 9999",
    waGreeting: "Buongiorno Dottoressa, posso fissare un appuntamento?",
  },
];

const SECTOR_ICONS = {
  scissors: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" /></svg>,
  sparkles: <Sparkles className="w-6 h-6" />,
  tooth: <Shield className="w-6 h-6" />,
  heart: <Star className="w-6 h-6" />,
  activity: <Zap className="w-6 h-6" />,
  paw: <CheckCircle2 className="w-6 h-6" />,
  dumbbell: <TrendingUp className="w-6 h-6" />,
  brain: <Users className="w-6 h-6" />,
};

// ============================================================
// HELPERS
// ============================================================
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const getNextDays = () => {
  const days = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
  const result = [];
  const now = new Date();
  for (let i = 1; i <= 5; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    result.push({ day: days[d.getDay()], date: d.getDate(), month: d.getMonth() + 1 });
  }
  return result;
};

const MOCK_SLOTS = ["09:30", "10:00", "11:30", "14:00", "15:30", "16:00", "17:00"];

// ============================================================
// MAIN COMPONENT
// ============================================================
const DemoLive = () => {
  const { language } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [phase, setPhase] = useState("sectors"); // sectors | demo
  const [sector, setSector] = useState(null);
  const [step, setStep] = useState(0);
  const [stepAnimState, setStepAnimState] = useState("enter");
  const stepStartRef = useRef(Date.now());

  const TOTAL_STEPS = 9; // Welcome(0), Problem(1), Booking(2), WhatsApp(3), Recovery(4), AntiNoShow(5), Reactivation(6), Result(7), CTA(8)

  const track = useCallback((event, meta = {}) => {
    axios.post(`${API}/analytics/track`, { event, referrer: JSON.stringify({ sector: sector?.key, step, ...meta }) }).catch(() => {});
  }, [sector, step]);

  useEffect(() => {
    axios.post(`${API}/analytics/track`, { event: "demo_live_view" }).catch(() => {});
  }, []);

  const selectSector = (s) => {
    setSector(s);
    setPhase("demo");
    setStep(0);
    track("demo_live_start", { sector: s.key });
  };

  const goNext = () => {
    const elapsed = Date.now() - stepStartRef.current;
    track("demo_live_step", { from: step, time_ms: elapsed });
    setStepAnimState("exit");
    setTimeout(() => {
      setStep((p) => Math.min(p + 1, TOTAL_STEPS - 1));
      setStepAnimState("enter");
      stepStartRef.current = Date.now();
    }, 300);
  };

  const goPrev = () => {
    if (step === 0) { setPhase("sectors"); setSector(null); return; }
    setStepAnimState("exit");
    setTimeout(() => {
      setStep((p) => Math.max(p - 1, 0));
      setStepAnimState("enter");
      stepStartRef.current = Date.now();
    }, 300);
  };

  const exitDemo = () => {
    track("demo_live_exit", { at_step: step });
    setPhase("sectors");
    setSector(null);
    setStep(0);
  };

  // ============================================================
  // SECTOR SELECTION SCREEN
  // ============================================================
  if (phase === "sectors") {
    return (
      <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden" data-testid="demo-live-sectors">
        {/* Ambient background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-emerald-500/5 blur-[120px]" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-blue-500/5 blur-[120px]" />
        </div>

        {/* Nav */}
        <nav className="relative z-10 flex items-center justify-between px-5 py-4 max-w-6xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <img src="/smartbooker-icon-48.png" alt="" className="h-7 w-7" />
            <span className="font-semibold text-lg">SmartBooker</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/onboarding">
              <Button size="sm" className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-white text-xs">
                Prova Gratuita
              </Button>
            </Link>
          </div>
        </nav>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-5 pt-12 pb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium mb-6">
              <Play className="w-3.5 h-3.5" /> Demo Interattiva
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Scopri come <span className="text-emerald-400">riempire automaticamente</span><br className="hidden sm:block" /> la tua agenda
            </h1>
            <p className="text-slate-400 mt-4 text-base sm:text-lg max-w-2xl mx-auto">
              Seleziona il tuo settore e vedi in 3 minuti come SmartBooker trasforma le richieste in appuntamenti confermati.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {SECTORS.map((s) => (
              <button
                key={s.key}
                onClick={() => selectSector(s)}
                className="group relative p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-emerald-500/40 hover:bg-emerald-500/[0.05] transition-all duration-300 text-left"
                data-testid={`sector-${s.key}`}
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  {SECTOR_ICONS[s.icon]}
                </div>
                <p className="font-medium text-sm text-white/90">{s.label}</p>
                <p className="text-xs text-slate-500 mt-1">{s.businessName}</p>
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 absolute top-5 right-4 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // DEMO WALKTHROUGH
  // ============================================================
  const s = sector;
  const animClass = stepAnimState === "enter"
    ? "animate-in fade-in slide-in-from-right-4 duration-500"
    : "animate-out fade-out slide-out-to-left-4 duration-300";

  return (
    <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden" data-testid="demo-live-walkthrough">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-slate-800">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-700 ease-out"
          style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
        />
      </div>

      {/* Top bar */}
      <div className="fixed top-1 left-0 right-0 z-40 px-4 py-2.5 flex items-center justify-between max-w-5xl mx-auto">
        <div className="flex items-center gap-3">
          <button onClick={goPrev} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="demo-prev">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-slate-500 font-medium">
            {step + 1} / {TOTAL_STEPS}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-600 hidden sm:inline">{s.businessName}</span>
          <button onClick={exitDemo} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="demo-exit">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Step content */}
      <div className={`min-h-screen flex items-center justify-center px-4 pt-14 pb-8 ${animClass}`}>
        {step === 0 && <StepWelcome sector={s} onNext={goNext} />}
        {step === 1 && <StepProblem sector={s} onNext={goNext} />}
        {step === 2 && <StepBooking sector={s} onNext={goNext} />}
        {step === 3 && <StepWhatsApp sector={s} onNext={goNext} />}
        {step === 4 && <StepRecovery sector={s} onNext={goNext} />}
        {step === 5 && <StepAntiNoShow sector={s} onNext={goNext} />}
        {step === 6 && <StepReactivation sector={s} onNext={goNext} />}
        {step === 7 && <StepResult sector={s} onNext={goNext} />}
        {step === 8 && <StepCTA sector={s} onRestart={() => { setStep(0); }} track={track} />}
      </div>
    </div>
  );
};

// ============================================================
// STEP 0 — WELCOME
// ============================================================
const StepWelcome = ({ sector, onNext }) => (
  <div className="max-w-lg w-full text-center space-y-8" data-testid="step-welcome">
    <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center mx-auto">
      {SECTOR_ICONS[sector.icon]}
    </div>
    <div className="space-y-3">
      <h2 className="text-2xl sm:text-3xl font-bold leading-tight">
        Scopri come riempire<br />automaticamente la tua agenda
      </h2>
      <p className="text-slate-400 text-sm sm:text-base">
        In meno di 3 minuti vedrai come trasformare ogni richiesta in un appuntamento confermato per <strong className="text-white">{sector.businessName}</strong>
      </p>
    </div>
    <button
      onClick={onNext}
      className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold text-base shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all"
      data-testid="start-demo-btn"
    >
      <Play className="w-5 h-5" /> Inizia demo
    </button>
    <p className="text-[11px] text-slate-600">Nessuna registrazione richiesta</p>
  </div>
);

// ============================================================
// STEP 1 — PROBLEM
// ============================================================
const StepProblem = ({ sector, onNext }) => {
  const [notifs, setNotifs] = useState([]);
  const [missed, setMissed] = useState(0);

  useEffect(() => {
    const msgs = [
      { name: sector.clientName, text: sector.waGreeting, delay: 600 },
      { name: "Alessia", text: "Ciao, quanto costa " + sector.services[0].name.toLowerCase() + "?", delay: 1800 },
      { name: "Marco", text: "Avete posto domani?", delay: 3200 },
      { name: "Sofia", text: "Vorrei prenotare...", delay: 4500 },
    ];
    const timers = msgs.map((m, i) =>
      setTimeout(() => {
        setNotifs((p) => [...p, m]);
        if (i > 0) setMissed((p) => p + 1);
      }, m.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [sector]);

  return (
    <div className="max-w-lg w-full space-y-8" data-testid="step-problem">
      {/* Mock phone with notifications */}
      <div className="mx-auto w-[280px] sm:w-[320px] rounded-[2rem] bg-slate-900 border border-slate-700/50 p-4 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs text-slate-500">WhatsApp</span>
          {missed > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold animate-pulse">
              {missed} non letti
            </span>
          )}
        </div>
        <div className="space-y-2.5 min-h-[180px]">
          {notifs.map((n, i) => (
            <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-xl bg-slate-800/60 animate-in slide-in-from-bottom-2 duration-300">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-3.5 h-3.5 text-emerald-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-white/80">{n.name}</p>
                <p className="text-[11px] text-slate-400 truncate">{n.text}</p>
              </div>
            </div>
          ))}
        </div>
        {/* Empty agenda slots */}
        <div className="mt-4 pt-3 border-t border-slate-700/50">
          <p className="text-[10px] text-slate-600 mb-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> Agenda di oggi</p>
          {["09:00", "10:00", "11:00", "14:00", "15:00"].map((t) => (
            <div key={t} className="flex items-center gap-2 py-1">
              <span className="text-[10px] text-slate-600 w-10">{t}</span>
              <div className="flex-1 h-5 rounded bg-slate-800/40 border border-dashed border-slate-700/30" />
            </div>
          ))}
        </div>
      </div>

      <div className="text-center space-y-3">
        <h2 className="text-xl sm:text-2xl font-bold">
          Ogni giorno ricevi richieste...<br />
          <span className="text-red-400">ma non tutte diventano appuntamenti</span>
        </h2>
        <p className="text-slate-400 text-sm">Messaggi persi, telefonate non risposte, slot vuoti in agenda.</p>
      </div>

      <div className="text-center">
        <button onClick={onNext} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 hover:bg-white/15 font-medium text-sm transition-colors" data-testid="step-problem-next">
          Continua <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// STEP 2 — BOOKING SIMULATION
// ============================================================
const StepBooking = ({ sector, onNext }) => {
  const [phase, setPhase] = useState(0); // 0=idle 1=service 2=date 3=time 4=confirm 5=done
  const [selService, setSelService] = useState(null);
  const [selDate, setSelDate] = useState(null);
  const [selTime, setSelTime] = useState(null);
  const days = getNextDays();

  useEffect(() => {
    const run = async () => {
      await wait(800);  setPhase(1);
      await wait(1200); setSelService(sector.services[0]); setPhase(2);
      await wait(1000); setSelDate(days[1]); setPhase(3);
      await wait(1000); setSelTime(MOCK_SLOTS[2]); setPhase(4);
      await wait(1000); setPhase(5);
    };
    run();
  }, [sector]);

  return (
    <div className="max-w-lg w-full space-y-6" data-testid="step-booking">
      {/* Simulated booking card */}
      <div className="mx-auto w-full max-w-[360px] rounded-2xl bg-slate-900 border border-slate-700/50 overflow-hidden shadow-2xl">
        <div className="px-5 py-4 bg-emerald-500/10 border-b border-slate-700/50 flex items-center gap-3">
          <Calendar className="w-5 h-5 text-emerald-400" />
          <div>
            <p className="text-sm font-semibold">{sector.businessName}</p>
            <p className="text-[11px] text-slate-400">Prenota il tuo appuntamento</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {/* Services */}
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">1. Scegli servizio</p>
            <div className="space-y-1.5">
              {sector.services.slice(0, 3).map((sv, i) => (
                <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg text-xs transition-all duration-500 ${selService?.name === sv.name ? "bg-emerald-500/15 border border-emerald-500/30 ring-1 ring-emerald-500/20" : phase >= 1 ? "bg-slate-800/50 border border-transparent" : "bg-slate-800/20 border border-transparent opacity-40"}`}>
                  <span className="font-medium">{sv.name}</span>
                  <span className="text-slate-400">{sv.duration}min — {sv.price}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Date */}
          {phase >= 2 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">2. Scegli data</p>
              <div className="flex gap-2">
                {days.slice(0, 4).map((d, i) => (
                  <div key={i} className={`flex-1 text-center p-2 rounded-lg text-xs transition-all duration-500 ${selDate === d ? "bg-emerald-500/15 border border-emerald-500/30" : "bg-slate-800/50 border border-transparent"}`}>
                    <p className="text-slate-500 text-[10px]">{d.day}</p>
                    <p className="font-bold">{d.date}/{d.month}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Time */}
          {phase >= 3 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">3. Scegli orario</p>
              <div className="flex flex-wrap gap-1.5">
                {MOCK_SLOTS.slice(0, 5).map((t) => (
                  <span key={t} className={`px-3 py-1.5 rounded-full text-xs transition-all duration-500 ${selTime === t ? "bg-emerald-500 text-white font-semibold" : "bg-slate-800/50 text-slate-400"}`}>{t}</span>
                ))}
              </div>
            </div>
          )}
          {/* Confirm */}
          {phase >= 4 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 pt-2">
              <div className={`w-full py-2.5 rounded-xl text-center text-sm font-semibold transition-all duration-700 ${phase >= 5 ? "bg-emerald-500 text-white" : "bg-emerald-500/50 text-white/70"}`}>
                {phase >= 5 ? (
                  <span className="flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4" /> Prenotazione Confermata!</span>
                ) : "Conferma..."}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="text-center space-y-3">
        <h2 className="text-xl sm:text-2xl font-bold">
          Il cliente prenota in <span className="text-emerald-400">pochi secondi</span>
        </h2>
        <p className="text-slate-400 text-sm">Senza chiamarti, senza messaggi, senza attese.</p>
      </div>

      <div className="text-center">
        <button onClick={onNext} className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all ${phase >= 5 ? "bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/20" : "bg-white/10 hover:bg-white/15 text-white/60"}`} data-testid="step-booking-next">
          Continua <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// STEP 3 — WHATSAPP SIMULATION
// ============================================================
const StepWhatsApp = ({ sector, onNext }) => {
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [done, setDone] = useState(false);
  const chatRef = useRef(null);

  useEffect(() => {
    const svc = sector.services[0];
    const slot = MOCK_SLOTS[3];
    const flow = [
      { role: "customer", text: sector.waGreeting, delay: 800 },
      { role: "bot_typing", delay: 1800 },
      { role: "bot", text: `Ciao ${sector.clientName}! Benvenuto/a da ${sector.businessName}.\n\nChe servizio ti interessa?\n\n${sector.services.map((s, i) => `${i + 1}. ${s.name} (${s.duration}min — ${s.price})`).join("\n")}`, delay: 2200 },
      { role: "customer_audio", text: "🎙️ 0:04 · Audio vocale", delay: 3800 },
      { role: "bot_typing", delay: 4400 },
      { role: "bot", text: `Ho capito: vuoi prenotare ${svc.name}.\n\nPer quando ti serve?\n\n1. Domani\n2. Dopodomani\n3. Questa settimana`, delay: 5000 },
      { role: "customer", text: "1", delay: 6500 },
      { role: "bot_typing", delay: 7200 },
      { role: "bot", text: `Domani abbiamo disponibili:\n\n1. ${MOCK_SLOTS[1]}\n2. ${MOCK_SLOTS[2]}\n3. ${slot}\n\nQuale preferisci?`, delay: 7600 },
      { role: "customer", text: "3", delay: 9000 },
      { role: "bot_typing", delay: 9600 },
      { role: "bot", text: `Prenotazione confermata!\n\n${svc.name}\nDomani alle ${slot}\n${sector.businessName}\n\nTi aspettiamo!`, delay: 10200 },
    ];

    const timers = flow.map((msg) =>
      setTimeout(() => {
        if (msg.role === "bot_typing") {
          setTyping(true);
        } else {
          setTyping(false);
          setMessages((p) => [...p, { role: msg.role, text: msg.text }]);
        }
      }, msg.delay)
    );
    const doneTimer = setTimeout(() => setDone(true), 11500);
    return () => { timers.forEach(clearTimeout); clearTimeout(doneTimer); };
  }, [sector]);

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  return (
    <div className="max-w-lg w-full space-y-6" data-testid="step-whatsapp">
      {/* WhatsApp phone mockup */}
      <div className="mx-auto w-full max-w-[360px] rounded-2xl bg-slate-900 border border-slate-700/50 overflow-hidden shadow-2xl relative">
        {/* NEW AI sticker - floating top-right */}
        <div
          className="absolute -top-2 -right-2 z-10 px-2.5 py-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold shadow-lg shadow-fuchsia-500/40 flex items-center gap-1 animate-pulse"
          data-testid="demo-ai-sticker"
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-white" />
          NEW · AI + Audio
        </div>
        {/* WA header */}
        <div className="px-4 py-3 bg-[#075E54] flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">{sector.businessName}</p>
            <p className="text-[10px] text-emerald-200">online</p>
          </div>
        </div>
        {/* Chat */}
        <div ref={chatRef} className="p-3 space-y-2 h-[320px] overflow-y-auto bg-[#0B141A]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}>
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "customer" || m.role === "customer_audio" ? "justify-end" : "justify-start"} animate-in slide-in-from-bottom-2 duration-200`}>
              {m.role === "customer_audio" ? (
                <div className="max-w-[80%] px-3 py-2 rounded-xl rounded-tr-sm bg-[#005C4B] text-white flex items-center gap-2 text-[12px]">
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-white">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </span>
                  <span className="flex-1 flex items-center gap-0.5">
                    {[3, 8, 5, 10, 6, 12, 4, 9, 6, 3, 7, 5, 8].map((h, j) => (
                      <span key={j} className="inline-block w-0.5 rounded-full bg-white/70" style={{ height: `${h}px` }} />
                    ))}
                  </span>
                  <span className="text-[10px] text-white/80">0:04</span>
                </div>
              ) : (
                <div className={`max-w-[80%] px-3 py-2 rounded-xl text-[12px] leading-relaxed whitespace-pre-line ${m.role === "customer" ? "bg-[#005C4B] text-white rounded-tr-sm" : "bg-[#1F2C34] text-slate-200 rounded-tl-sm"}`}>
                  {m.text}
                </div>
              )}
            </div>
          ))}
          {typing && (
            <div className="flex justify-start animate-in fade-in duration-200">
              <div className="bg-[#1F2C34] px-4 py-2.5 rounded-xl rounded-tl-sm flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="text-center space-y-2">
        <h2 className="text-xl sm:text-2xl font-bold">
          Il sistema risponde, propone e <span className="text-emerald-400">conferma automaticamente</span>
        </h2>
        <p className="text-slate-500 text-xs font-medium uppercase tracking-wider">Tu non devi intervenire</p>
        <p className="text-[11px] font-medium text-fuchsia-300/90 inline-flex items-center gap-1.5 justify-center">
          <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" />
          AI + Audio WhatsApp supportati (gpt-4o-mini + Whisper)
        </p>
      </div>

      <div className="text-center">
        <button onClick={onNext} className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all ${done ? "bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/20" : "bg-white/10 hover:bg-white/15 text-white/60"}`} data-testid="step-wa-next">
          Continua <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// STEP 4 — RECOVERY
// ============================================================
const StepRecovery = ({ sector, onNext }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const run = async () => {
      await wait(800);  setPhase(1); // show abandoned
      await wait(2500); setPhase(2); // show recovery msg
      await wait(2500); setPhase(3); // show client reply
      await wait(1500); setPhase(4); // booked
    };
    run();
  }, []);

  return (
    <div className="max-w-lg w-full space-y-6" data-testid="step-recovery">
      <div className="mx-auto w-full max-w-[360px] rounded-2xl bg-slate-900 border border-slate-700/50 overflow-hidden shadow-2xl">
        <div className="px-4 py-3 bg-amber-500/10 border-b border-slate-700/50 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium">Recovery automatica</span>
        </div>
        <div className="p-5 space-y-4">
          {/* Abandoned conversation */}
          {phase >= 1 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 p-3 rounded-xl bg-red-500/5 border border-red-500/10">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-red-400">Prenotazione abbandonata</p>
                  <p className="text-[11px] text-slate-400 mt-1">{sector.clientName} ha iniziato ma non ha completato</p>
                  <p className="text-[10px] text-slate-600 mt-1">Servizio: {sector.services[1].name} — Si è fermato alla scelta orario</p>
                </div>
              </div>
            </div>
          )}
          {/* Auto recovery message */}
          {phase >= 2 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <p className="text-[10px] text-amber-500 font-medium mb-2 flex items-center gap-1"><Zap className="w-3 h-3" /> SmartBooker interviene automaticamente</p>
              <div className="bg-[#1F2C34] p-3 rounded-xl text-[12px] text-slate-200 leading-relaxed">
                Ciao {sector.clientName}! Abbiamo notato che stavi prenotando un {sector.services[1].name}.<br /><br />Vuoi completare la prenotazione? Abbiamo ancora posti domani alle {MOCK_SLOTS[3]}.
              </div>
            </div>
          )}
          {/* Client responds */}
          {phase >= 3 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex justify-end">
              <div className="bg-[#005C4B] px-3 py-2 rounded-xl rounded-tr-sm text-[12px] text-white">
                Si perfetto, confermo!
              </div>
            </div>
          )}
          {/* Confirmed */}
          {phase >= 4 && (
            <div className="animate-in fade-in scale-in-95 duration-500 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
              <p className="text-xs font-semibold text-emerald-400">Prenotazione recuperata!</p>
              <p className="text-[10px] text-slate-400 mt-1">{sector.services[1].name} — Domani {MOCK_SLOTS[3]}</p>
            </div>
          )}
        </div>
      </div>

      <div className="text-center space-y-3">
        <h2 className="text-xl sm:text-2xl font-bold">
          Se un cliente si ferma, <span className="text-amber-400">SmartBooker lo riporta a prenotare</span>
        </h2>
        <p className="text-slate-400 text-sm">Automaticamente, senza che tu debba fare nulla.</p>
      </div>

      <div className="text-center">
        <button onClick={onNext} className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all ${phase >= 4 ? "bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/20" : "bg-white/10 hover:bg-white/15 text-white/60"}`} data-testid="step-recovery-next">
          Continua <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// STEP 5 — ANTI NO-SHOW
// ============================================================
const StepAntiNoShow = ({ sector, onNext }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const run = async () => {
      await wait(600);  setPhase(1); // appointment card
      await wait(1500); setPhase(2); // 24h reminder sent
      await wait(1800); setPhase(3); // confirmation request
      await wait(2000); setPhase(4); // client confirms
      await wait(1200); setPhase(5); // 2h reminder
      await wait(1500); setPhase(6); // done
    };
    run();
  }, []);

  return (
    <div className="max-w-lg w-full space-y-6" data-testid="step-noshow">
      <div className="mx-auto w-full max-w-[360px] rounded-2xl bg-slate-900 border border-slate-700/50 overflow-hidden shadow-2xl">
        <div className="px-4 py-3 bg-violet-500/10 border-b border-slate-700/50 flex items-center gap-2">
          <Shield className="w-4 h-4 text-violet-400" />
          <span className="text-sm font-medium">Sistema Anti No-Show</span>
        </div>
        <div className="p-5 space-y-3">
          {/* Appointment */}
          {phase >= 1 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 p-3 rounded-xl bg-slate-800/60 border border-slate-700/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-emerald-400" />
                  <div>
                    <p className="text-xs font-medium">{sector.clientName} — {sector.services[0].name}</p>
                    <p className="text-[10px] text-slate-500">Domani alle {MOCK_SLOTS[2]}</p>
                  </div>
                </div>
                <span className="px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 text-[10px] font-medium">In attesa</span>
              </div>
            </div>
          )}
          {/* 24h reminder */}
          {phase >= 2 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex items-start gap-2.5 p-2.5 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
              <div className="w-7 h-7 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Bell className="w-3.5 h-3.5 text-emerald-400" />
              </div>
              <div>
                <p className="text-[10px] text-emerald-500 font-medium">Reminder 24h inviato</p>
                <p className="text-[11px] text-slate-300 mt-0.5">
                  "Ciao {sector.clientName}, ti ricordiamo l'appuntamento di domani alle {MOCK_SLOTS[2]} per {sector.services[0].name}"
                </p>
              </div>
            </div>
          )}
          {/* Confirmation request */}
          {phase >= 3 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex items-start gap-2.5 p-2.5 rounded-xl bg-amber-500/5 border border-amber-500/10">
              <div className="w-7 h-7 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <div>
                <p className="text-[10px] text-amber-500 font-medium">Richiesta conferma inviata</p>
                <p className="text-[11px] text-slate-300 mt-0.5">"Confermi la tua presenza? Rispondi SI o clicca il link"</p>
              </div>
            </div>
          )}
          {/* Client confirms */}
          {phase >= 4 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex justify-end">
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/15 border border-emerald-500/20">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <div>
                  <p className="text-[11px] font-medium text-emerald-400">Confermato dal cliente</p>
                  <p className="text-[10px] text-slate-400">{sector.clientName} ha confermato</p>
                </div>
              </div>
            </div>
          )}
          {/* 2h final reminder */}
          {phase >= 5 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex items-start gap-2.5 p-2.5 rounded-xl bg-blue-500/5 border border-blue-500/10">
              <div className="w-7 h-7 rounded-full bg-blue-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Clock className="w-3.5 h-3.5 text-blue-400" />
              </div>
              <div>
                <p className="text-[10px] text-blue-500 font-medium">Reminder 2h prima</p>
                <p className="text-[11px] text-slate-300 mt-0.5">"Tra 2 ore! Ti aspettiamo alle {MOCK_SLOTS[2]}"</p>
              </div>
            </div>
          )}
          {/* Result badge */}
          {phase >= 6 && (
            <div className="animate-in fade-in scale-in-95 duration-500 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center">
              <p className="text-xs font-semibold text-emerald-400 flex items-center justify-center gap-1.5">
                <Shield className="w-4 h-4" /> No-show evitato
              </p>
              <p className="text-[10px] text-slate-400 mt-1">Il cliente si presenta puntuale</p>
            </div>
          )}
        </div>
      </div>

      <div className="text-center space-y-3">
        <h2 className="text-xl sm:text-2xl font-bold">
          Riduci i buchi in agenda con <span className="text-violet-400">reminder automatici</span>
        </h2>
        <p className="text-slate-400 text-sm">Reminder, conferma e promemoria finale. Tutto automatico.</p>
      </div>

      <div className="text-center">
        <button onClick={onNext} className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all ${phase >= 6 ? "bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/20" : "bg-white/10 hover:bg-white/15 text-white/60"}`} data-testid="step-noshow-next">
          Continua <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// STEP 6 — REACTIVATION (CRM)
// ============================================================
const StepReactivation = ({ sector, onNext }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const run = async () => {
      await wait(600);  setPhase(1); // CRM view
      await wait(1800); setPhase(2); // inactive filter applied
      await wait(1800); setPhase(3); // auto message sent
      await wait(2000); setPhase(4); // client replies
      await wait(1500); setPhase(5); // booked
    };
    run();
  }, []);

  const inactiveClients = [
    { name: "Anna V.", days: 67, service: sector.services[0].name },
    { name: "Roberto M.", days: 52, service: sector.services[1].name },
    { name: "Elena P.", days: 45, service: sector.services[2]?.name || sector.services[0].name },
  ];

  return (
    <div className="max-w-lg w-full space-y-6" data-testid="step-reactivation">
      <div className="mx-auto w-full max-w-[360px] rounded-2xl bg-slate-900 border border-slate-700/50 overflow-hidden shadow-2xl">
        <div className="px-4 py-3 bg-purple-500/10 border-b border-slate-700/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium">CRM Clienti</span>
          </div>
          {phase >= 2 && (
            <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 text-[10px] font-medium animate-in fade-in duration-300">
              Inattivi da 45+ giorni
            </span>
          )}
        </div>
        <div className="p-4 space-y-3">
          {/* Client list */}
          {phase >= 1 && (
            <div className="space-y-2">
              {inactiveClients.map((c, i) => (
                <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg transition-all duration-500 ${phase >= 2 ? "bg-red-500/5 border border-red-500/10" : "bg-slate-800/50 border border-transparent"} animate-in fade-in slide-in-from-bottom-1 duration-300`} style={{ animationDelay: `${i * 150}ms` }}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-[10px] font-bold text-slate-300">{c.name[0]}</div>
                    <div>
                      <p className="text-xs font-medium">{c.name}</p>
                      <p className="text-[10px] text-slate-500">Ultimo: {c.service}</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-medium ${phase >= 2 ? "text-red-400" : "text-slate-500"}`}>{c.days}gg fa</span>
                </div>
              ))}
            </div>
          )}
          {/* Auto message */}
          {phase >= 3 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <p className="text-[10px] text-purple-500 font-medium mb-2 flex items-center gap-1"><Zap className="w-3 h-3" /> Messaggio automatico inviato</p>
              <div className="bg-[#1F2C34] p-3 rounded-xl text-[12px] text-slate-200 leading-relaxed">
                Ciao {inactiveClients[0].name.split(" ")[0]}! Sono passati {inactiveClients[0].days} giorni dalla tua ultima visita da {sector.businessName}.<br /><br />Ti va di prenotare? Abbiamo disponibilità questa settimana.
              </div>
            </div>
          )}
          {/* Client responds */}
          {phase >= 4 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex justify-end">
              <div className="bg-[#005C4B] px-3 py-2 rounded-xl rounded-tr-sm text-[12px] text-white">
                Ciao! Si, vero, sono sparita. Giovedi pomeriggio avete posto?
              </div>
            </div>
          )}
          {/* Booked */}
          {phase >= 5 && (
            <div className="animate-in fade-in scale-in-95 duration-500 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
              <p className="text-xs font-semibold text-emerald-400">Cliente riattivata!</p>
              <p className="text-[10px] text-slate-400 mt-1">{inactiveClients[0].name} — {sector.services[0].name} Giovedi {MOCK_SLOTS[4]}</p>
            </div>
          )}
        </div>
      </div>

      <div className="text-center space-y-3">
        <h2 className="text-xl sm:text-2xl font-bold">
          Recupera clienti che <span className="text-purple-400">non tornano da mesi</span>
        </h2>
        <p className="text-slate-400 text-sm">Il CRM identifica chi non viene da tempo e li ricontatta automaticamente.</p>
      </div>

      <div className="text-center">
        <button onClick={onNext} className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all ${phase >= 5 ? "bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/20" : "bg-white/10 hover:bg-white/15 text-white/60"}`} data-testid="step-reactivation-next">
          Continua <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// STEP 7 — RESULT DASHBOARD
// ============================================================
const StepResult = ({ sector, onNext }) => {
  const [counters, setCounters] = useState({ bookings: 0, clients: 0, revenue: 0, noshow: 85 });

  useEffect(() => {
    const targets = { bookings: 147, clients: 89, revenue: sector.services[0].price * 147, noshow: 12 };
    const duration = 2000;
    const steps = 40;
    const interval = duration / steps;
    let i = 0;
    const timer = setInterval(() => {
      i++;
      const progress = i / steps;
      setCounters({
        bookings: Math.round(targets.bookings * progress),
        clients: Math.round(targets.clients * progress),
        revenue: Math.round(targets.revenue * progress),
        noshow: Math.round(85 - (85 - targets.noshow) * progress),
      });
      if (i >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, [sector]);

  return (
    <div className="max-w-lg w-full space-y-8" data-testid="step-result">
      <div className="text-center space-y-3">
        <h2 className="text-2xl sm:text-3xl font-bold">
          <span className="text-emerald-400">Più clienti.</span> Meno tempo perso.<br /><span className="text-emerald-400">Più fatturato.</span>
        </h2>
        <p className="text-slate-400 text-sm">Ecco cosa ottieni con SmartBooker attivo per {sector.businessName}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
          <Calendar className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="text-2xl font-bold text-emerald-400">{counters.bookings}</p>
          <p className="text-[11px] text-slate-400">Prenotazioni/mese</p>
        </div>
        <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
          <Users className="w-5 h-5 text-blue-400 mb-2" />
          <p className="text-2xl font-bold text-blue-400">{counters.clients}</p>
          <p className="text-[11px] text-slate-400">Clienti attivi</p>
        </div>
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <TrendingUp className="w-5 h-5 text-amber-400 mb-2" />
          <p className="text-2xl font-bold text-amber-400">{counters.revenue.toLocaleString("it-IT")}</p>
          <p className="text-[11px] text-slate-400">Fatturato/mese</p>
        </div>
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
          <Shield className="w-5 h-5 text-red-400 mb-2" />
          <p className="text-2xl font-bold text-red-400">-{counters.noshow}%</p>
          <p className="text-[11px] text-slate-400">No-show ridotti</p>
        </div>
      </div>

      {/* Mini agenda filled */}
      <div className="p-4 rounded-xl bg-slate-900 border border-slate-700/50">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> La tua agenda domani</p>
        {["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "14:00", "14:30"].map((t, i) => (
          <div key={t} className="flex items-center gap-2 py-0.5">
            <span className="text-[10px] text-slate-600 w-10">{t}</span>
            <div className="flex-1 h-5 rounded bg-emerald-500/15 border border-emerald-500/10 px-2 flex items-center">
              <span className="text-[9px] text-emerald-400 truncate">{sector.services[i % sector.services.length].name}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="text-center">
        <button onClick={onNext} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all" data-testid="step-result-next">
          Vedi come attivarlo <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// STEP 8 — FINAL CTA
// ============================================================
const StepCTA = ({ sector, onRestart, track }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleCTA = (type) => {
    track("demo_live_cta", { type });
  };

  const submitContact = async () => {
    if (!form.name.trim() || !form.phone.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads/submit`, {
        name: form.name, phone: form.phone, sector: sector.key,
        message: form.message || "Richiesta dalla demo live",
        source_page: "demo_live"
      });
      setSent(true);
      track("demo_live_contact_form");
    } catch {}
    finally { setSending(false); }
  };

  return (
    <div className="max-w-lg w-full text-center space-y-8" data-testid="step-cta">
      <div className="space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center mx-auto">
          <Sparkles className="w-8 h-8" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold leading-tight">
          Ora immagina questo sistema<br />nella <span className="text-emerald-400">tua attività</span>
        </h2>
        <p className="text-slate-400 text-sm max-w-md mx-auto">
          Ogni giorno, clienti che prenotano da soli, nessuna telefonata persa, nessun buco in agenda. SmartBooker lavora per te 24/7.
        </p>
      </div>

      {/* CTAs */}
      <div className="space-y-3">
        <Link to="/onboarding" onClick={() => handleCTA("free_trial")}>
          <button className="w-full max-w-xs mx-auto flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-base shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all" data-testid="cta-free-trial">
            Attiva prova gratuita <ArrowRight className="w-5 h-5" />
          </button>
        </Link>
        <p className="text-[11px] text-slate-600">14 giorni gratis. Nessuna carta richiesta.</p>

        <div className="flex items-center justify-center gap-3 pt-2">
          <a href="https://wa.me/393770873260?text=Ciao%2C%20ho%20appena%20visto%20la%20demo%20di%20SmartBooker%20e%20vorrei%20saperne%20di%20pi%C3%B9" target="_blank" rel="noopener noreferrer" onClick={() => handleCTA("whatsapp_expert")}>
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] text-sm font-medium border border-[#25D366]/20 transition-colors" data-testid="cta-whatsapp">
              <MessageSquare className="w-4 h-4" /> Parla con un esperto
            </button>
          </a>
          <button onClick={() => { setShowForm(true); handleCTA("contact_form"); }} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-white/80 text-sm font-medium border border-white/10 transition-colors" data-testid="cta-contact">
            <Send className="w-4 h-4" /> Richiedi info
          </button>
        </div>
      </div>

      {/* Contact form */}
      {showForm && !sent && (
        <div className="mx-auto max-w-xs p-5 rounded-2xl bg-slate-900 border border-slate-700/50 space-y-3 animate-in fade-in slide-in-from-bottom-3 duration-300 text-left">
          <p className="text-sm font-semibold">Ti ricontattiamo noi</p>
          <input value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))} placeholder="Il tuo nome" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm focus:outline-none focus:border-emerald-500/50" data-testid="contact-name" />
          <input value={form.phone} onChange={(e) => setForm(p => ({...p, phone: e.target.value}))} placeholder="Telefono" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm focus:outline-none focus:border-emerald-500/50" data-testid="contact-phone" />
          <textarea value={form.message} onChange={(e) => setForm(p => ({...p, message: e.target.value}))} placeholder="Messaggio (opzionale)" rows={2} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm resize-none focus:outline-none focus:border-emerald-500/50" />
          <button onClick={submitContact} disabled={sending || !form.name.trim() || !form.phone.trim()} className="w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold disabled:opacity-50 transition-colors" data-testid="contact-submit">
            {sending ? "Invio..." : "Invia"}
          </button>
        </div>
      )}
      {sent && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 animate-in fade-in duration-300">
          <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
          <p className="text-sm font-semibold text-emerald-400">Messaggio inviato!</p>
          <p className="text-xs text-slate-400 mt-1">Ti ricontattiamo entro 24 ore.</p>
        </div>
      )}

      {/* Restart */}
      <button onClick={onRestart} className="text-xs text-slate-600 hover:text-slate-400 transition-colors flex items-center gap-1 mx-auto mt-4" data-testid="restart-demo">
        <RefreshCw className="w-3 h-3" /> Rivedi demo
      </button>
    </div>
  );
};

export default DemoLive;
